import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import * as workerTimers from "worker-timers";
import AuthContext from "./contexts/AuthContext";
import Page404 from "../src/pages/page404";
import Logout from "./pages/Auth/Logout";
import UnathenticatedRoutes from "./routes/UnauthenticatedRoutes";
import AuthenticatedRoutes from "./routes/AuthenticatedRoutes";

// Design System imports
import "./design-system/tokens/index.css";
import { ThemeProvider, ToastProvider, AppConfigProvider } from "./design-system";

// Legacy styles (to be migrated)
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";

import KonfhubLoad from "./components/KonfhubLoad";
import { Auth } from "aws-amplify";
import { setJwtToken } from "./axios";
import { listEvents } from "./common/api";
import {addAllEventsToIndexDB} from "./common/functions";
import { usePWAInstallPrompt } from "./common/usePWAInstallPrompt";
import InstallPrompt from "./components/InstallPrompt";

function App(props) {
  const [state, setState] = useState({
    user: { attributes: {} },
    signedIn: null,
    refreshToken: null,
    domain: null,
  });
  const [eventUpdate, setEventUpdate] = useState(false);
  const [eventDetails, setEventDetails] = useState({});
  const [apiError, setApiError] = useState(null);
  const location = useLocation();

  // PWA Install Prompt
  const { isInstallPromptSupported, showInstallPrompt, isAppInstalled, isIOSDevice } = usePWAInstallPrompt();

  const checkSession = async () => {
    let user = { attributes: {} },
      signedIn = false;
    try {
      user = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      signedIn = true;
      setJwtToken(user.signInUserSession.idToken.jwtToken);
      refreshToken();
    } catch (err) {
      console.log("cs -- error log -- ", err);
    }
    setState({...state, user: {...state.user, ...user}, signedIn});
  };

  const refreshToken = async () => {
    let refreshToken = workerTimers.setInterval(async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        user.refreshSession(session.refreshToken, (err, session) => {
          if (err) {
            clearInterval(refreshToken);
            console.log("cs -- refresh error -- ", err);
            window.location.href = "/logout";
            return;
          }
          if (session?.idToken) {
            setJwtToken(session.idToken.jwtToken);
          }
        });
      } catch (err) {
        clearInterval(refreshToken);
        console.log("cs -- error log -- ", err);
        window.location.href = "/logout";
      }
    }, 60 * 60 * 1000);
    setState({ ...state, refreshToken });
  };

  // iOS viewport height fix and keyboard detection
  useEffect(() => {
    // Detect if device is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      document.documentElement.setAttribute('data-ios', 'true');
    }

    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    // Set initial height
    setViewportHeight();

    // iOS Keyboard detection using visualViewport API
    const viewport = window.visualViewport;
    let initialHeight = window.innerHeight;

    const handleViewportChange = () => {
      if (!viewport || !isIOS) return;

      // Calculate if keyboard is likely open (viewport shrunk significantly)
      const heightDiff = initialHeight - viewport.height;
      const isKeyboardOpen = heightDiff > 150; // Keyboard typically > 150px

      // Set attribute on html element for CSS targeting
      if (isKeyboardOpen) {
        document.documentElement.setAttribute('data-ios-keyboard', 'open');
      } else {
        document.documentElement.removeAttribute('data-ios-keyboard');
        // Update initial height when keyboard closes (in case orientation changed)
        initialHeight = window.innerHeight;
      }
    };

    // Update on resize and orientation change
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', () => {
      setViewportHeight();
      initialHeight = window.innerHeight;
    });

    // Listen to visualViewport for keyboard detection
    if (viewport && isIOS) {
      viewport.addEventListener('resize', handleViewportChange);
      viewport.addEventListener('scroll', handleViewportChange);
    }

    // Handle focus/blur on inputs for additional keyboard detection
    const handleFocus = (e) => {
      if (!isIOS) return;
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Small delay to let iOS keyboard animate in
        setTimeout(() => {
          document.documentElement.setAttribute('data-ios-keyboard', 'open');
        }, 100);
      }
    };

    const handleBlur = (e) => {
      if (!isIOS) return;
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Small delay to let iOS keyboard animate out
        setTimeout(() => {
          // Only remove if no other input is focused
          if (document.activeElement?.tagName !== 'INPUT' &&
              document.activeElement?.tagName !== 'TEXTAREA') {
            document.documentElement.removeAttribute('data-ios-keyboard');
          }
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocus, true);
    document.addEventListener('focusout', handleBlur, true);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
      document.removeEventListener('focusin', handleFocus, true);
      document.removeEventListener('focusout', handleBlur, true);
      if (viewport && isIOS) {
        viewport.removeEventListener('resize', handleViewportChange);
        viewport.removeEventListener('scroll', handleViewportChange);
      }
    };
  }, []);

  useEffect(()=> {
    listAllEvents();
    let locationUrl = window.location.href;
    if(locationUrl.includes("sponsorEventCode")){
      const sponsorCodePram = new URLSearchParams(location.search).get('sponsorEventCode');
      localStorage.setItem("sponsorCodePreFill", sponsorCodePram);
    }
  },[])

  useEffect(() => {
    checkSession();
  }, [location]);

  const listAllEvents = async () => {
    listEvents()
      .then((res) => {
        let allEventDetails = JSON.parse(res.data.listEventsForSponsor);
        if (allEventDetails.statusCode === 200) {
          setApiError(null);
          if(allEventDetails.body.length === 1){
            addAllEventsToIndexDB(allEventDetails.body);
            setEventUpdate("Special Event for Volunteers");
            setEventDetails({...eventDetails,
              eventId: allEventDetails.body[0].eventId,
              sponsorCode: allEventDetails.body[0].sponsorCode,
              removeHome: true
            });
          }
          else if(allEventDetails.body.length === 0){
            setEventUpdate(allEventDetails.body);
            setEventDetails({...eventDetails,
              removeHome: true
            });
          }
          else{
            setEventUpdate(allEventDetails.body);
          }
        }
        else {
          // Handle API error - set error state and allow UI to render
          const errorMessage = allEventDetails.body || "Error loading events. Please try again.";
          setApiError(errorMessage);
          setEventUpdate([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setApiError("Failed to connect to server. Please check your connection.");
        setEventUpdate([]);
      });
  };

  const setAuth = async (user = {}, signedIn = true) => {
    clearInterval(state.refreshToken);
    if (signedIn) {
      refreshToken();
      await listAllEvents();
    }
  };

  return (
    <ThemeProvider>
      <AppConfigProvider>
        <ToastProvider>
          {/* PWA Install Prompt */}
          {!isAppInstalled && isInstallPromptSupported && (
            <InstallPrompt onInstall={showInstallPrompt} isIOSDevice={isIOSDevice} />
          )}
          <AuthContext.Provider
            value={{
              state,
              setValue: setState,
              setAuth: setAuth,
              refreshToken,
              setEventUpdate,
              eventUpdate,
              setEventDetails,
              eventDetails,
              apiError,
              setApiError
            }}
          >
            <React.Suspense fallback={<KonfhubLoad />}>
              <Routes>
                <Route path="/logout" element={<Logout />} />
                <Route path="/404" element={<Page404 />} />
                {state.signedIn === null ? (
                  <Route path="*" element={<KonfhubLoad />} />
                ) : state.signedIn ? (
                  <Route path="*" element={<AuthenticatedRoutes state={state} />} />
                ) : state.signedIn === false ? (
                  <Route
                    path="*"
                    element={
                      <UnathenticatedRoutes
                        signedIn={state.signedIn}
                        user={state.user}
                      />
                    }
                  />
                ) : (
                  <></>
                )}
              </Routes>
            </React.Suspense>
          </AuthContext.Provider>
        </ToastProvider>
      </AppConfigProvider>
    </ThemeProvider>
  );
}

export default App;
